/**
 * VLE Consent Page
 *
 * Phase 2: VLE Dashboard with consent table, filters, and create form.
 * Replaces Phase 1 profile page with full dashboard functionality.
 *
 * Features:
 * - Consent list table (filtered by VLE's createdbyUserId)
 * - Filters: Date range, Mobile search, Status
 * - Create Statement drawer (5-field form)
 * - Payment and Share action buttons
 *
 * @author Claude Code
 * @date February 2026
 * @doc Reference: Doc 32 - Phase 2 Implementation
 */

import React, { useEffect, useState, useCallback } from 'react';
import { Button, DrawerProvider } from 'equal-ds-ui';
import { LogOut, RefreshCw } from 'lucide-react';
import { Toast } from '../../ui/Toast';
import { vleAPI } from '../../../services/vle-api.service';
import { useErrorHandler } from '../../../hooks/useErrorHandler';
import { VleConsentTable } from './VleConsentTable';
import { VleConsentFilters } from './VleConsentFilters';
import { CreateStatementDrawer } from './CreateStatementDrawer';
import { OneAppConsentModal } from './OneAppConsentModal';
import type { PageComponentProps } from '../../../registry/react-component-registry.constants';
import type {
  VleConsentRecord,
  VleFilterState,
  VleConsentCreateResponse,
} from './types';
import { DEFAULT_PAGE_SIZE } from './types';

interface VleUserInfo {
  id: number;
  name: string;
  email: string;
  csc_id: string;
  role: string;
  role_id: number;
  role_name: string;
}

const INITIAL_FILTERS: VleFilterState = {
  dateFrom: '',
  dateTo: '',
  mobileSearch: '',
  status: '',
};

/**
 * Validate a backend-provided URL before using it for a top-level navigation or
 * a form POST. Both the consent redirect (`webRedirectionUrl`) and the payment
 * gateway (`pay_url`) come from the backend and carry the same trust level, so
 * they share this check — guards against a tampered value redirecting the flow
 * to an unintended target. Requires https for real targets; allows http ONLY for
 * a local mock (localhost/127.0.0.1) so local end-to-end testing works — a
 * production URL is always https and never localhost, so this stays safe.
 */
const isSafeExternalUrl = (raw: string): boolean => {
  let url: URL;
  try {
    url = new URL(raw);
  } catch {
    return false;
  }
  const isLocalhost = ['localhost', '127.0.0.1'].includes(url.hostname);
  return url.protocol === 'https:' || (url.protocol === 'http:' && isLocalhost);
};

const getUserInfo = (): VleUserInfo | null => {
  try {
    const encoded = sessionStorage.getItem('loggedUserInfo');
    if (!encoded) {
      return null;
    }
    return JSON.parse(atob(encoded));
  } catch (error) {
    return null;
  }
};

export const VleConsentPage: React.FC<PageComponentProps> = ({ queryParams }) => {
  const { handleError } = useErrorHandler();

  // User state
  const [userInfo, setUserInfo] = useState<VleUserInfo | null>(null);
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  // Data state
  const [consents, setConsents] = useState<VleConsentRecord[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  // Filter state
  const [filters, setFilters] = useState<VleFilterState>(INITIAL_FILTERS);
  // `filters` (above) is the draft edited live in the panel. `appliedFilters` is the
  // committed set that actually drives the query — updated only on Apply/Reset — so
  // typing in the filter fields never triggers a fetch.
  const [appliedFilters, setAppliedFilters] = useState<VleFilterState>(INITIAL_FILTERS);

  // OneApp (CSC/VLE) iframe modal — opened when the create response is provider === 'ONEAPP'.
  const [oneAppModal, setOneAppModal] = useState<{ url: string; consentHandle: string } | null>(null);

  // Drawer state
  const [isCreateDrawerOpen, setIsCreateDrawerOpen] = useState(false);

  // Toast state
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [toastVariant, setToastVariant] = useState<'success' | 'error' | 'warning'>('success');

  // Payment status refresh state
  const [refreshingConsentId, setRefreshingConsentId] = useState<string | null>(null);

  // Reversal state (CSC Phase 2)
  const [reversingConsentId, setReversingConsentId] = useState<string | null>(null);

  /**
   * Load user info on mount and handle payment callback params
   */
  useEffect(() => {
    // Payment callback params (forwarded by CscCallbackComponent after a PG return)
    const paymentSuccess = queryParams?.payment_success;
    const paymentFailed = queryParams?.payment_failed;
    const paymentError = queryParams?.payment_error;
    const paymentConsentId = queryParams?.consent_id;

    // Handle payment callback messages
    if (paymentSuccess === 'true') {
      showToast(`Payment successful for consent ${paymentConsentId || ''}`, 'success');
    } else if (paymentFailed === 'true') {
      const errorMsg = queryParams?.payment_error || 'Payment failed';
      showToast(`Payment failed: ${errorMsg}`, 'error');
    } else if (paymentError) {
      showToast(`Payment error: ${paymentError}`, 'error');
    }

    // Credentials are established solely by CscCallbackComponent (the OAuth callback)
    // before this page is ever reached. This page deliberately does NOT ingest
    // token/userInfo from the URL — doing so was a session-fixation vector (any link
    // to /dashboard/vle-consent?token=... could overwrite the active session). If no
    // session exists, the Angular auth guard on the route redirects to login.
    setUserInfo(getUserInfo());
  }, [queryParams]);

  /**
   * Load consents when user info is available or filters/pagination change
   */
  useEffect(() => {
    if (userInfo) {
      loadConsents();
    }
  }, [userInfo, currentPage, pageSize, appliedFilters]);

  /**
   * Fetch consents from API
   */
  const loadConsents = useCallback(async () => {
    if (!userInfo) return;

    setIsLoading(true);
    try {
      const params: any = {
        pageno: currentPage,
        limit: pageSize,
      };

      // Add committed filters if set
      if (appliedFilters.dateFrom) params.from = appliedFilters.dateFrom;
      if (appliedFilters.dateTo) params.to = appliedFilters.dateTo;
      if (appliedFilters.mobileSearch) params.mobile_no = appliedFilters.mobileSearch;
      if (appliedFilters.status) params.status = appliedFilters.status;

      const response = await vleAPI.getConsentList(params);

      if (response?.data) {
        setConsents(response.data.rows || []);
        setTotalCount(response.data.count || 0);
      }
    } catch (error) {
      handleError(error, { component: 'VleConsentPage', action: 'loadConsents' });
      showToast('Failed to load consents', 'error');
    } finally {
      setIsLoading(false);
    }
  }, [userInfo, currentPage, pageSize, appliedFilters, handleError]);

  /**
   * Handle filter apply
   */
  const handleFilterApply = useCallback(() => {
    setAppliedFilters(filters); // commit the draft; the effect performs the reload
    setCurrentPage(1);          // reset to first page
  }, [filters]);

  /**
   * Handle filter reset
   */
  const handleFilterReset = useCallback(() => {
    setFilters(INITIAL_FILTERS);        // clear the visible fields
    setAppliedFilters(INITIAL_FILTERS); // commit cleared filters; the effect reloads
    setCurrentPage(1);
  }, []);

  /**
   * Handle page change
   */
  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
  }, []);

  /**
   * Handle page size change
   */
  const handlePageSizeChange = useCallback((size: number) => {
    setPageSize(size);
    setCurrentPage(1);
  }, []);

  /**
   * Handle refresh
   */
  const handleRefresh = useCallback(() => {
    loadConsents();
  }, [loadConsents]);

  /**
   * Handle consent creation success
   */
  const handleCreateSuccess = useCallback((response: VleConsentCreateResponse) => {
    showToast('Consent request created successfully', 'success');

    // If redirect URL is provided, redirect to AA for OTP verification
    // AIDEV-NOTE: API returns webRedirectionUrl (not redirect_url) - matching ConsentRequestPage pattern
    if (response.data?.webRedirectionUrl) {
      // Validate before a full top-level navigation — same trust level and check
      // as pay_url below (guards against a tampered backend value redirecting the
      // customer to an unintended target).
      if (!isSafeExternalUrl(response.data.webRedirectionUrl)) {
        showToast('Invalid redirect URL', 'error');
        // Consent was still created — refresh so it shows in the list.
        loadConsents();
        return;
      }
      // OneApp: open the journey in an in-dashboard iframe modal (so we can catch the SUCCESS
      // event and sync the data). OneMoney: full-page redirect, as before.
      if (response.data.provider === 'ONEAPP' && response.data.consent_handle && response.data.webRedirectionUrl) {
        setOneAppModal({ url: response.data.webRedirectionUrl, consentHandle: response.data.consent_handle });
        return;
      }
      window.location.href = response.data.webRedirectionUrl;
    } else {
      // Reload consents to show new entry
      loadConsents();
    }
  }, [loadConsents]);

  /**
   * Handle payment click - initiates payment flow
   */
  const handlePaymentClick = useCallback(async (consent: VleConsentRecord) => {
    if (!consent.consent_id) {
      showToast('Cannot initiate payment - no consent ID', 'error');
      return;
    }

    // Check if already paid
    if (consent.payment_status === 'SUCCESS') {
      showToast('Payment already completed for this consent', 'error');
      return;
    }

    showToast('Initiating payment...', 'success');

    try {
      const response = await vleAPI.initiatePayment(
        consent.consent_id,
        consent.identifier_value
      );

      if (response.data?.pay_url && response.data?.message) {
        // Validate pay_url before POSTing the encrypted payment payload to it —
        // guards against a tampered backend/config value redirecting the payment
        // (and message) to an unintended target. Same check as webRedirectionUrl.
        if (!isSafeExternalUrl(response.data.pay_url)) {
          showToast('Invalid payment gateway URL', 'error');
          return;
        }

        // Build payment form programmatically and submit to CSC Bridge PG
        // [SOURCE-TRUTH] files/10-integration-code.md — same pattern as .NET/Java/PHP reference implementations
        const form = document.createElement('form');
        form.method = 'post';
        form.action = response.data.pay_url;
        form.style.display = 'none';
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = 'message';
        input.value = response.data.message;
        form.appendChild(input);
        document.body.appendChild(form);
        form.submit();
      } else {
        showToast('Payment initiation failed - no payment data received', 'error');
      }
    } catch (error) {
      handleError(error, { component: 'VleConsentPage', action: 'initiatePayment' });
      showToast('Failed to initiate payment. Please try again.', 'error');
    }
  }, [handleError]);

  /**
   * Handle refresh payment status click — calls CSC Transaction Status API
   * [SOURCE-TRUTH] FinproCSC/files/06-api-transaction-status.md
   */
  const handleRefreshPaymentStatus = useCallback(async (consent: VleConsentRecord) => {
    if (!consent.consent_id) {
      showToast('Cannot check status: consent ID not available', 'error');
      return;
    }

    setRefreshingConsentId(consent.consent_id);

    try {
      const response = await vleAPI.checkPaymentStatus(consent.consent_id);
      const data = response.data;

      if (!data) {
        showToast('Unable to get payment status', 'error');
        return;
      }

      // Show toast based on payment status
      if (data.too_early) {
        showToast(data.status_description || 'Please wait at least 5 seconds', 'warning');
      } else if (data.payment_status === 'SUCCESS') {
        showToast(data.status_description || 'Payment successful!', 'success');
      } else if (data.payment_status === 'FAILED') {
        showToast(data.status_description || 'Payment failed', 'error');
      } else if (data.payment_status === 'UNKNOWN') {
        showToast(data.status_description || 'Payment is still being processed', 'warning');
      } else if (data.csc_error) {
        showToast(data.status_description || 'Status check failed', 'error');
      } else {
        showToast(data.status_description || 'Status checked', 'success');
      }

      // Update the consent in-place (no full page reload)
      setConsents(prev => prev.map(c => {
        if (c.consent_id === consent.consent_id) {
          return {
            ...c,
            payment_status: data.payment_status,
            payment_txn_status: data.txn_status,
            payment_txn_amount: data.txn_amount ? parseFloat(data.txn_amount) : c.payment_txn_amount,
          };
        }
        return c;
      }));

    } catch (error) {
      showToast('Unable to reach payment gateway. Please try again.', 'error');
    } finally {
      setRefreshingConsentId(null);
    }
  }, []);

  /**
   * Handle reverse payment click — calls CSC Transaction Reversal API
   * AIDEV-NOTE: CSC Phase 2 — Transaction Reversal (API 2 of 5)
   * [SOURCE-TRUTH] FinproCSC/files/07-api-reversal-refund.md
   */
  const handleReversePayment = useCallback(async (consent: VleConsentRecord) => {
    if (!consent.consent_id) {
      showToast('Cannot reverse: consent ID not available', 'error');
      return;
    }

    setReversingConsentId(consent.consent_id);

    try {
      const response = await vleAPI.reversePayment(consent.consent_id);
      const data = response.data;

      if (!data) {
        showToast('Unable to process reversal', 'error');
        return;
      }

      // Show toast based on result
      if (data.success && data.reversal_status === 'COMPLETED') {
        showToast(data.message || 'Reversal completed successfully', 'success');
      } else if (data.error_reason === 'too_early') {
        showToast(data.message || 'Too early — please wait at least 120 seconds', 'warning');
      } else if (data.error_reason === 'different_day') {
        showToast(data.message || 'Too late — reversals only on same day', 'error');
      } else if (data.error_reason === 'already_reversed') {
        showToast(data.message || 'Reversal already submitted', 'warning');
      } else if (data.error_reason === 'wrong_status') {
        showToast(data.message || 'Only UNKNOWN transactions can be reversed', 'error');
      } else {
        showToast(data.message || 'Reversal declined', 'error');
      }

      // Update consent in-place if reversal was processed
      if (data.reversal_status) {
        setConsents(prev => prev.map(c => {
          if (c.consent_id === consent.consent_id) {
            return {
              ...c,
              payment_status: data.reversal_status === 'COMPLETED' ? 'REVERSED' : 'REVERSAL_DECLINED',
            };
          }
          return c;
        }));
      }

    } catch (error) {
      showToast('Unable to process reversal. Please try again.', 'error');
    } finally {
      setReversingConsentId(null);
    }
  }, []);

  /**
   * Handle share click - Send statement PDF link via SMS (Phase 5)
   * AIDEV-NOTE: Uses Mock SMS for testing - check shared_url_records table for the short URL
   */
  const handleShareClick = useCallback(async (consent: VleConsentRecord) => {
    if (!consent.consent_id) {
      showToast('Cannot share: consent ID not available', 'error');
      return;
    }

    const mobile = consent.identifier_value;
    if (!mobile) {
      showToast('Cannot share: mobile number not available', 'error');
      return;
    }

    try {
      showToast('Sending statement link...', 'success');

      const response = await vleAPI.shareStatement(consent.consent_id, mobile);

      // AIDEV-NOTE: AdminServer returns { status: 'success', data: { sms_sent, remaining_sms_count } }
      // Simplified check - just verify status is success
      if (response.status === 'success') {
        const remaining = response.data?.remaining_sms_count ?? 0;
        showToast(`Statement link sent to ${mobile}! (${remaining} SMS remaining)`, 'success');
      } else {
        const errorMsg = response.errorMessage || response.message || 'Failed to send SMS';
        showToast(errorMsg, 'error');
      }
    } catch (error: any) {
      const errorMsg = error?.response?.data?.errorMessage || error?.message || 'Failed to share statement';
      showToast(errorMsg, 'error');
      handleError(error, { component: 'VleConsentPage', action: 'shareStatement' });
    }
  }, [handleError]);

  /**
   * Handle logout
   */
  const handleLogout = useCallback(async () => {
    setIsLoggingOut(true);

    try {
      // Use the service layer (httpClient) so the base URL and token rotation are
      // handled consistently; it's best-effort — we clear the session regardless.
      await vleAPI.logout();

      // Clear session storage
      sessionStorage.removeItem('finpro_k_Authorization');
      sessionStorage.removeItem('loggedUserInfo');
      sessionStorage.removeItem('vle_csc_data');

      showToast('Logged out successfully', 'success');

      setTimeout(() => {
        window.location.href = '/login';
      }, 1500);

    } catch (error) {
      showToast('Logout failed. Please try again.', 'error');
      setIsLoggingOut(false);
    }
  }, []);

  /**
   * Show toast message
   */
  const showToast = (message: string, variant: 'success' | 'error' | 'warning') => {
    setToastMessage(message);
    setToastVariant(variant);
  };

  // Loading state
  if (!userInfo && isLoading) {
    return (
      <div className="h-full flex flex-col p-4 space-y-4">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span className="ml-3 text-gray-600">Loading VLE dashboard...</span>
        </div>
      </div>
    );
  }

  // Session expired
  if (!userInfo) {
    return (
      <div className="h-full flex flex-col p-4 space-y-4">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <h2 className="text-lg font-semibold text-red-800 mb-2">Session Expired</h2>
          <p className="text-red-600 mb-4">Your session has expired. Please log in again.</p>
          <Button
            onClick={() => window.location.href = '/login'}
            variant="primary"
          >
            Go to Login
          </Button>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="h-full flex flex-col p-4 space-y-4">
        {/* Page Header */}
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900 tracking-wide">
              VLE Dashboard
            </h1>
            <p className="text-sm text-gray-600 mt-1">
              Welcome, {userInfo.name}. Manage your statement requests from this dashboard.
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              variant="secondary"
              size="sm"
              onClick={handleRefresh}
              disabled={isLoading}
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={handleLogout}
              disabled={isLoggingOut}
            >
              <LogOut className="w-4 h-4 mr-2" />
              {isLoggingOut ? 'Logging out...' : 'Logout'}
            </Button>
          </div>
        </div>

        {/* Toast Messages */}
        {toastMessage && (
          <Toast
            message={toastMessage}
            variant={toastVariant}
            duration={4000}
            onClose={() => setToastMessage(null)}
          />
        )}

        {/* Filters */}
        <VleConsentFilters
          filters={filters}
          onFilterChange={setFilters}
          onApply={handleFilterApply}
          onReset={handleFilterReset}
          onCreateClick={() => setIsCreateDrawerOpen(true)}
          isLoading={isLoading}
        />

        {/* Consent Table */}
        <div className="flex-1 bg-white rounded-lg border border-gray-200 shadow-sm p-4">
          <VleConsentTable
            consents={consents}
            isLoading={isLoading}
            totalCount={totalCount}
            currentPage={currentPage}
            pageSize={pageSize}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
            onRefresh={handleRefresh}
            onPaymentClick={handlePaymentClick}
            onShareClick={handleShareClick}
            onRefreshPaymentStatus={handleRefreshPaymentStatus}
            refreshingConsentId={refreshingConsentId}
            onReversePayment={handleReversePayment}
            reversingConsentId={reversingConsentId}
          />
        </div>
      </div>

      {/* Create Statement Drawer - Separate DrawerProvider wrapping ONLY the drawer */}
      <DrawerProvider
        side="right"
        open={isCreateDrawerOpen}
        onOpenChange={setIsCreateDrawerOpen}
      >
        <CreateStatementDrawer
          isOpen={isCreateDrawerOpen}
          onClose={() => setIsCreateDrawerOpen(false)}
          onSuccess={handleCreateSuccess}
          vleUserId={userInfo.id.toString()}
        />
      </DrawerProvider>

      {oneAppModal && (
        <OneAppConsentModal
          webRedirectionUrl={oneAppModal.url}
          consentHandle={oneAppModal.consentHandle}
          onClose={() => setOneAppModal(null)}
          onComplete={(result) => {
            if (result?.data?.has_data) showToast('Statement is ready', 'success');
            else showToast('No data found for this request', 'warning');
            loadConsents();
          }}
          onError={(msg) => { showToast(msg, 'error'); loadConsents(); }}
        />
      )}
    </>
  );
};

export default VleConsentPage;
